import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react'
import React, { Suspense, useEffect } from 'react'

import { POSTHOG_HOST, POSTHOG_KEY } from '@goodfellas/environment'

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!(posthog as { __loaded?: boolean }).__loaded) {
      const initOptions = {
        api_host: POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'always' as const,
        session_recording: {
          captureCanvas: false, // avoids Canvas2D getImageData warning; runtime accepts this
        },
      }
      posthog.init(
        POSTHOG_KEY as string,
        initOptions as unknown as Parameters<typeof posthog.init>[1],
      )
    }
  }, [])

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  )
}

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      if (searchParams.toString()) {
        url = `${url}?${searchParams.toString()}`
      }

      posthog.capture('$pageview', { $current_url: url })
    }
  }, [pathname, searchParams, posthog])

  return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}
